import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Channel, DefaultGenerics } from 'stream-chat'
import { ChatService } from '@/services/Chat/ChatService'
import { ChannelOptions, type UnreadCounts } from '../types'
import { UNREAD_QUERY_HARD_CAP } from '../constants'
import { queryChannelsWithRetry } from '../services/ChatApiService'
import {
  shouldIncrementUnread,
  getChannelTypeFromEvent,
  playNotificationSound,
} from '../services/chatEventHandlers'
import { ChatClientContextV2 } from './ChatClientProviderV2'

export interface ChatUnreadContextV2Value {
  unreadCounts: UnreadCounts
  refreshUnreadCounts: () => Promise<void>
}

const defaultValue: ChatUnreadContextV2Value = {
  unreadCounts: { total: 0, direct: 0, case: 0 },
  refreshUnreadCounts: async () => {},
}

export const ChatUnreadContextV2 =
  createContext<ChatUnreadContextV2Value>(defaultValue)

export const ChatUnreadProviderV2: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { client, currentUserId, connectionState } =
    useContext(ChatClientContextV2)
  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({
    total: 0,
    direct: 0,
    case: 0,
  })

  const refreshUnreadCounts = useCallback(async () => {
    if (!client || !currentUserId) return

    const limit = 30
    let offset = 0
    let allChannels: Channel<DefaultGenerics>[] = []

    // Iterative pagination with hard cap (fixes bug #6: recursive unread fetch)
    while (offset < UNREAD_QUERY_HARD_CAP) {
      // eslint-disable-next-line no-await-in-loop
      const channels = await queryChannelsWithRetry(
        client,
        {
          type: 'messaging',
          members: { $in: [currentUserId] },
          has_unread: true,
        },
        {},
        { limit, offset }
      )

      allChannels = [...allChannels, ...channels]

      if (channels.length < limit) break
      offset += limit
    }

    const counts = allChannels
      .filter(channel => !channel.data?.archived)
      .reduce(
        (acc, channel) => {
          // A channel whose unread messages are all system messages still
          // matches has_unread server-side but must contribute 0 here
          const unreadCount = ChatService.countVisibleUnread(
            channel,
            currentUserId
          )
          if (channel.data?.channel_type === ChannelOptions.DirectMessages) {
            acc.direct += unreadCount
          } else if (
            channel.data?.channel_type === ChannelOptions.CaseMessages
          ) {
            acc.case += unreadCount
          }
          return acc
        },
        { direct: 0, case: 0 }
      )

    setUnreadCounts({
      total: counts.direct + counts.case,
      direct: counts.direct,
      case: counts.case,
    })
  }, [client, currentUserId])

  // Init unread counts on connect
  useEffect(() => {
    if (connectionState.status === 'connected') {
      refreshUnreadCounts()
    }
  }, [connectionState.status, refreshUnreadCounts])

  // Listen for new message events (fixes bugs #1, #2, #3)
  useEffect(() => {
    if (!client) return undefined

    const { unsubscribe } = client.on(event => {
      if (
        event.type !== 'message.new' &&
        event.type !== 'notification.message_new'
      ) {
        if (event.type === 'notification.mark_read') {
          // Refresh counts when a channel is marked read
          refreshUnreadCounts()
        }
        return
      }

      // Bug fix #2: guard check runs BEFORE mutation
      const isViewingMessages = window.location.pathname.startsWith('/messages')
      const activeChannelId =
        new URLSearchParams(window.location.search).get('channelId') ?? null

      if (
        !shouldIncrementUnread(
          event,
          currentUserId,
          activeChannelId,
          isViewingMessages
        )
      ) {
        return
      }

      // Bug fix #3: read channel type from event payload (no API call)
      const channelType = getChannelTypeFromEvent(event)

      // Bug fix #1: functional state updates (no stale closure)
      if (channelType === ChannelOptions.DirectMessages) {
        setUnreadCounts(prev => ({
          ...prev,
          direct: prev.direct + 1,
          total: prev.total + 1,
        }))
      } else if (channelType === ChannelOptions.CaseMessages) {
        setUnreadCounts(prev => ({
          ...prev,
          case: prev.case + 1,
          total: prev.total + 1,
        }))
      }

      playNotificationSound()
    })

    return () => {
      unsubscribe()
    }
  }, [client, currentUserId, refreshUnreadCounts])

  const value = useMemo(
    () => ({ unreadCounts, refreshUnreadCounts }),
    [unreadCounts, refreshUnreadCounts]
  )

  return (
    <ChatUnreadContextV2.Provider value={value}>
      {children}
    </ChatUnreadContextV2.Provider>
  )
}
